import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
    baseURL: API_BASE,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Add auth token to requests if available
api.interceptors.request.use((config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// Handle 401 responses (auto logout)
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            localStorage.removeItem('auth_token');
            localStorage.removeItem('auth_user');
            // Only redirect if we're in admin area
            if (window.location.pathname.startsWith('/admin')) {
                window.location.href = '/login';
            }
        }
        return Promise.reject(error);
    }
);

// Helper function to extract error message from API response
export const getErrorMessage = (error, fallback = 'Ein Fehler ist aufgetreten') => {
    // Check for 403 (permission denied)
    if (error.response?.status === 403) {
        return error.response?.data?.detail || 'Keine Berechtigung';
    }
    // Check for other API errors with detail message
    if (error.response?.data?.detail) {
        return error.response.data.detail;
    }
    // Check for validation errors
    if (error.response?.data?.message) {
        return error.response.data.message;
    }
    // Fallback
    return fallback;
};

// =============================================================================
// AUTH
// =============================================================================

export const login = (email, password) => {
    const formData = new URLSearchParams();
    formData.append('username', email);
    formData.append('password', password);
    return api.post('/auth/login', formData, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
    });
};

export const loginJson = (email, password) => 
    api.post('/auth/login-json', { email, password });

export const getMe = () => api.get('/auth/me');

// =============================================================================
// USERS (Admin)
// =============================================================================

export const getUsers = () => api.get('/users');
export const createUser = (data) => api.post('/users', data);
export const updateUser = (id, data) => api.put(`/users/${id}`, data);
export const deleteUser = (id) => api.delete(`/users/${id}`);

// =============================================================================
// SETTINGS
// =============================================================================

export const getSettings = () => api.get('/settings');
export const updateSettings = (data) => api.put('/settings', data);
export const getPublicSettings = () => api.get('/public/settings');

// =============================================================================
// LEHRER PORTAL
// =============================================================================

export const verifyLehrerPin = (pin) => api.post('/lehrer/verify-pin', { pin });
export const identifyLehrer = (vorname, nachname, token) => 
    api.post('/lehrer/identify', { vorname, nachname }, { params: { token } });
export const getLehrerData = (token) => api.get('/lehrer/meine-daten', { params: { token } });
export const updateLehrerProfile = (data, token) => 
    api.put('/lehrer/mein-profil', data, { params: { token } });
export const createLehrerAngebot = (data, token) => 
    api.post('/lehrer/angebot', data, { params: { token } });
export const updateLehrerAngebot = (id, data, token) => 
    api.put(`/lehrer/angebot/${id}`, data, { params: { token } });
export const deleteLehrerAngebot = (id, token) => 
    api.delete(`/lehrer/angebot/${id}`, { params: { token } });

// =============================================================================
// LEHRPERSONEN STATUS (Admin)
// =============================================================================

export const getLehrerStatusOverview = () => api.get('/lehrpersonen/status/overview');

// =============================================================================
// DASHBOARD
// =============================================================================

export const getDashboardStats = () => api.get('/dashboard/stats');

// =============================================================================
// GEMEINDEN
// =============================================================================

export const getGemeinden = () => api.get('/gemeinden');
export const getGemeinde = (id) => api.get(`/gemeinden/${id}`);
export const createGemeinde = (data) => api.post('/gemeinden', data);
export const updateGemeinde = (id, data) => api.put(`/gemeinden/${id}`, data);
export const deleteGemeinde = (id) => api.delete(`/gemeinden/${id}`);
export const getGemeindenMitUnterrichtsorten = () => api.get('/gemeinden-mit-unterrichtsorten');

// =============================================================================
// INSTRUMENTENGRUPPEN
// =============================================================================

export const getInstrumentengruppen = () => api.get('/instrumentengruppen');
export const getInstrumentengruppe = (id) => api.get(`/instrumentengruppen/${id}`);
export const createInstrumentengruppe = (data) => api.post('/instrumentengruppen', data);
export const updateInstrumentengruppe = (id, data) => api.put(`/instrumentengruppen/${id}`, data);
export const deleteInstrumentengruppe = (id) => api.delete(`/instrumentengruppen/${id}`);

// =============================================================================
// INSTRUMENTE
// =============================================================================

export const getInstrumente = () => api.get('/instrumente');
export const getInstrument = (id) => api.get(`/instrumente/${id}`);
export const createInstrument = (data) => api.post('/instrumente', data);
export const updateInstrument = (id, data) => api.put(`/instrumente/${id}`, data);
export const deleteInstrument = (id) => api.delete(`/instrumente/${id}`);

// =============================================================================
// UNTERRICHTSORTE
// =============================================================================

export const getUnterrichtsorte = (gemeindeId = null) => {
    const params = gemeindeId ? { gemeinde_id: gemeindeId } : {};
    return api.get('/unterrichtsorte', { params });
};
export const getUnterrichtsort = (id) => api.get(`/unterrichtsorte/${id}`);
export const createUnterrichtsort = (data) => api.post('/unterrichtsorte', data);
export const updateUnterrichtsort = (id, data) => api.put(`/unterrichtsorte/${id}`, data);
export const deleteUnterrichtsort = (id) => api.delete(`/unterrichtsorte/${id}`);

// =============================================================================
// LEHRPERSONEN
// =============================================================================

export const getLehrpersonen = () => api.get('/lehrpersonen');
export const getLehrperson = (id) => api.get(`/lehrpersonen/${id}`);
export const createLehrperson = (data) => api.post('/lehrpersonen', data);
export const updateLehrperson = (id, data) => api.put(`/lehrpersonen/${id}`, data);
export const deleteLehrperson = (id) => api.delete(`/lehrpersonen/${id}`);

// Foto Upload/Delete for Lehrpersonen
export const uploadLehrpersonFoto = (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/lehrpersonen/${id}/foto`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    });
};
export const deleteLehrpersonFoto = (id) => api.delete(`/lehrpersonen/${id}/foto`);

// CSV Import for Lehrpersonen
export const downloadLehrpersonenTemplate = () => 
    api.get('/lehrpersonen/csv/template', { responseType: 'blob' });
export const importLehrpersonenCSV = (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/lehrpersonen/csv/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    });
};
export const swapLehrpersonNames = (id) => api.post(`/lehrpersonen/${id}/swap-names`);

// Photo ZIP Import for Lehrpersonen
export const importLehrpersonenFotosZip = (file, overwrite = false) => {
    const formData = new FormData();
    formData.append('file', file);
    const endpoint = overwrite 
        ? '/lehrpersonen/fotos/import-zip-overwrite' 
        : '/lehrpersonen/fotos/import-zip';
    return api.post(endpoint, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    });
};

// JSON Export/Import for Lehrpersonen (Full Backup)
export const exportLehrpersonenJSON = () => 
    api.get('/lehrpersonen/export/json', { responseType: 'blob' });
export const importLehrpersonenJSON = (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/lehrpersonen/import/json', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    });
};

// CSV Export for Lehrpersonen
export const exportLehrpersonenCSV = () => 
    api.get('/lehrpersonen/export/csv', { responseType: 'blob' });

// JSON Export/Import for Standorte (Gemeinden + Unterrichtsorte)
export const exportStandorteJSON = () => 
    api.get('/standorte/export/json', { responseType: 'blob' });
export const importStandorteJSON = (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/standorte/import/json', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    });
};

// =============================================================================
// RÄUME
// =============================================================================

export const getRaeume = () => api.get('/raeume');
export const getRaeumeByUnterrichtsort = (unterrichtsortId) => 
    api.get(`/raeume/by-unterrichtsort/${unterrichtsortId}`);
export const getRaum = (id) => api.get(`/raeume/${id}`);
export const createRaum = (data) => api.post('/raeume', data);
export const updateRaum = (id, data) => api.put(`/raeume/${id}`, data);
export const deleteRaum = (id) => api.delete(`/raeume/${id}`);
export const parseAnzeigename = (bezeichnung) => 
    api.post('/raeume/parse-anzeigename', { bezeichnung });
export const initStandardRaeume = () => api.post('/raeume/init-standard-raeume');

// =============================================================================
// DEPENDENCY CHECKS
// =============================================================================

export const checkGemeindeDependencies = (id) => api.get(`/dependencies/gemeinde/${id}`);
export const checkUnterrichtsortDependencies = (id) => api.get(`/dependencies/unterrichtsort/${id}`);
export const checkRaumDependencies = (id) => api.get(`/dependencies/raum/${id}`);
export const checkInstrumentDependencies = (id) => api.get(`/dependencies/instrument/${id}`);
export const migrateDependencies = (data) => api.post('/dependencies/migrate', data);

// =============================================================================
// ANGEBOTE
// =============================================================================

export const getAngebote = (filters = {}) => api.get('/angebote', { params: filters });
export const getAngebot = (id) => api.get(`/angebote/${id}`);
export const createAngebot = (data) => api.post('/angebote', data);
export const updateAngebot = (id, data) => api.put(`/angebote/${id}`, data);
export const deleteAngebot = (id) => api.delete(`/angebote/${id}`);

export default api;
